import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import ReviewForm from "@/components/ReviewForm";

const POLL_ATTEMPTS = 7;
const POLL_INTERVAL = 2000;

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
    const [details, setDetails] = useState(null);
    const attemptsRef = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setStatus("failed");
            return;
        }
        let cancelled = false;

        const poll = async () => {
            if (cancelled) return;
            try {
                const res = await api.get(`/checkout/status/${sessionId}`);
                setDetails(res.data);
                if (res.data.payment_status === "paid") {
                    setStatus("paid");
                    return;
                }
                if (res.data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                attemptsRef.current += 1;
                if (attemptsRef.current >= POLL_ATTEMPTS) {
                    setStatus("failed");
                    return;
                }
                setTimeout(poll, POLL_INTERVAL);
            } catch (e) {
                attemptsRef.current += 1;
                if (attemptsRef.current >= POLL_ATTEMPTS) {
                    setStatus("failed");
                    return;
                }
                setTimeout(poll, POLL_INTERVAL);
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    return (
        <div
            data-testid="checkout-success-page"
            className="min-h-screen bg-black text-white flex items-center justify-center px-6"
        >
            <div className="max-w-xl w-full text-center">
                {status === "checking" && (
                    <div data-testid="checkout-checking">
                        <Loader2 className="animate-spin mx-auto mb-6" size={40} />
                        <p className="label-mono">Verifying payment</p>
                        <h1 className="font-display text-4xl md:text-6xl uppercase mt-4">
                            One moment…
                        </h1>
                    </div>
                )}
                {status === "paid" && (
                    <div data-testid="checkout-paid">
                        <CheckCircle2 className="mx-auto mb-6 text-white" size={48} />
                        <p className="label-mono">— Order Confirmed</p>
                        <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tight mt-4">
                            Welcome to
                            <br />
                            <span className="italic">the family.</span>
                        </h1>
                        <p className="font-sans text-muted-foreground mt-6 max-w-md mx-auto">
                            Your piece is being prepared. You'll receive a confirmation email shortly with shipping details.
                        </p>
                        {details && (
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground mt-6">
                                Total: ${(details.amount_total / 100).toFixed(2)} {details.currency?.toUpperCase()}
                            </p>
                        )}

                        <ReviewForm
                            sessionId={sessionId}
                            productName={details?.metadata?.product_name}
                            productId={details?.metadata?.product_id}
                        />
                    </div>
                )}
                {status === "expired" && (
                    <div data-testid="checkout-expired">
                        <XCircle className="mx-auto mb-6 text-signal" size={40} />
                        <p className="label-mono">— Session Expired</p>
                        <h1 className="font-display text-4xl md:text-6xl uppercase mt-4">
                            Try again.
                        </h1>
                    </div>
                )}
                {status === "failed" && (
                    <div data-testid="checkout-failed">
                        <XCircle className="mx-auto mb-6 text-signal" size={40} />
                        <p className="label-mono">— Verification Timed Out</p>
                        <h1 className="font-display text-4xl md:text-6xl uppercase mt-4">
                            Check your email
                        </h1>
                        <p className="text-muted-foreground mt-4">
                            We could not verify the status in time. If the payment went through, you'll receive a confirmation.
                        </p>
                    </div>
                )}

                <Link
                    to="/"
                    data-testid="back-home-link"
                    className="inline-flex items-center gap-2 mt-12 font-mono text-xs uppercase tracking-[0.25em] border-b border-white/30 pb-1 hover:border-white"
                >
                    <ArrowLeft size={14} />
                    Back to Home
                </Link>
            </div>
        </div>
    );
}
